import React from 'react'
import PropTypes from 'prop-types'
import { Info } from '@phosphor-icons/react'
import { Trans, useTranslation } from 'react-i18next'

import RadioField from '../../FormLegacy/FormFieldsV2/RadioField'
import TextField from '../../FormLegacy/FormFieldsV2/TextField'
import TextAreaField from '../../FormLegacy/FormFieldsV2/TextAreaField'

const formatAmount = (amount) => `$${Number(amount || 0).toFixed(2)}`

const sanitizeRefundAmount = (rawValue, paidAmount) => {
  let value = rawValue.replace(/[^0-9.]/g, '')

  const firstDotIndex = value.indexOf('.')
  if (firstDotIndex !== -1) {
    value = value.slice(0, firstDotIndex + 1) + value.slice(firstDotIndex + 1).replace(/\./g, '')
  }

  const dotIndex = value.indexOf('.')
  if (dotIndex !== -1 && value.length > dotIndex + 3) {
    value = value.slice(0, dotIndex + 3)
  }

  if (value && Number(value) > paidAmount) {
    value = String(paidAmount)
  }

  return value
}

const ConfirmPopupBody = ({
  title,
  shouldRenderRefundOptions = false,
  allowPartialRefund = false,
  refundBlocked = false,
  paidAmount = 0,
  refundState = 'refund',
  refundAmount = '',
  cancelNote = '',
  onRefundStateChange = () => {},
  onRefundAmountChange = () => {},
  onCancelNoteChange = () => {},
}) => {
  const { t } = useTranslation(['reservations'])

  const handleRefundStateChange = (e) => {
    onRefundStateChange(e)
    if (e.target.value !== 'partial') {
      onRefundAmountChange('')
    }
  }

  const handleRefundAmountChange = (e) => {
    onRefundAmountChange(sanitizeRefundAmount(e.target.value, paidAmount))
  }

  const showPartialRefundError = refundState === 'partial' && refundAmount === '0'

  return (
    <div className="cancel-reservation-confirm-body">
      <span>{title}</span>
      {shouldRenderRefundOptions && allowPartialRefund && refundBlocked && (
        <React.Fragment>
          <div className="cancel-reservation-confirm-body__banner">
            <Info size={20} />
            <div className="cancel-reservation-confirm-body__banner-text">
              <div>
                <Trans
                  i18nKey="reservations:confirmPopupBody.refundUnavailableText"
                  components={[<a key="0" href="mailto:hello@elevatedliving.com" />]}
                />
              </div>
            </div>
          </div>
          <RadioField
            type="radio"
            name="cancelOption"
            label={t('reservations:conflictReservations.cancelWithoutRefund')}
            onChange={onRefundStateChange}
            checked={refundState === 'no_refund'}
            value="no_refund"
          />
          <div className="divider" />
        </React.Fragment>
      )}
      {shouldRenderRefundOptions && allowPartialRefund && !refundBlocked && (
        <React.Fragment>
          <RadioField
            type="radio"
            name="cancelOption"
            label={t('reservations:confirmPopupBody.cancelWithFullRefund', {
              amount: formatAmount(paidAmount),
            })}
            onChange={handleRefundStateChange}
            checked={refundState === 'refund'}
            value="refund"
          />
          <RadioField
            type="radio"
            name="cancelOption"
            label={t('reservations:confirmPopupBody.cancelWithPartialRefund')}
            onChange={handleRefundStateChange}
            checked={refundState === 'partial'}
            value="partial"
          />
          {refundState === 'partial' && (
            <TextField
              name="refundAmount"
              label={t('reservations:confirmPopupBody.refundAmountLabel')}
              placeholder={t('reservations:confirmPopupBody.enterRefundAmountPlaceholder')}
              value={refundAmount}
              onChange={handleRefundAmountChange}
              isError={showPartialRefundError}
              errorLabel={
                showPartialRefundError
                  ? t('reservations:confirmPopupBody.refundAmountMustBeGreaterThanZero')
                  : undefined
              }
            />
          )}
          <RadioField
            type="radio"
            name="cancelOption"
            label={t('reservations:conflictReservations.cancelWithoutRefund')}
            onChange={handleRefundStateChange}
            checked={refundState === 'no_refund'}
            value="no_refund"
          />
          <div className="divider" />
        </React.Fragment>
      )}
      {shouldRenderRefundOptions && !allowPartialRefund && (
        <React.Fragment>
          <RadioField
            type="radio"
            name="cancelOption"
            label={t('reservations:conflictReservations.cancelWithRefund')}
            onChange={onRefundStateChange}
            checked={refundState === 'refund'}
            value="refund"
          />
          <RadioField
            type="radio"
            name="cancelOption"
            label={t('reservations:conflictReservations.cancelWithoutRefund')}
            onChange={onRefundStateChange}
            checked={refundState === 'no_refund'}
            value="no_refund"
          />
          <div className="divider" />
        </React.Fragment>
      )}
      <TextAreaField
        name="cancelNote"
        label={t('reservations:confirmPopupBody.cancellationNoteLabel')}
        placeholder={t('reservations:confirmPopupBody.cancellationNotePlaceholder')}
        rows={3}
        value={cancelNote}
        onChange={onCancelNoteChange}
      />
    </div>
  )
}

ConfirmPopupBody.propTypes = {
  title: PropTypes.string.isRequired,
  shouldRenderRefundOptions: PropTypes.bool,
  allowPartialRefund: PropTypes.bool,
  refundBlocked: PropTypes.bool,
  paidAmount: PropTypes.number,
  refundState: PropTypes.string,
  refundAmount: PropTypes.string,
  cancelNote: PropTypes.string,
  onRefundStateChange: PropTypes.func,
  onRefundAmountChange: PropTypes.func,
  onCancelNoteChange: PropTypes.func,
}

export default ConfirmPopupBody
